import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { PhotoMark } from "./PhotoMark";

const PHOTOS = [
  { src: "/photos/cb-01.webp", caption: "«Con un solo corazón», en escena", span: "md:col-span-12 md:row-span-2" },
  { src: "/photos/cb-02.webp", caption: "Guitarras y voces en directo", span: "md:col-span-6" },
  { src: "/photos/cb-03.webp", caption: "El alma del trío", span: "md:col-span-6" },
  { src: "/photos/cb-04.webp", caption: "Boleros sobre el escenario", span: "md:col-span-6" },
  { src: "/photos/cb-05.webp", caption: "Noches de bolero", span: "md:col-span-6" },
  { src: "/photos/cb-06.webp", caption: "En directo, sin artificios", span: "md:col-span-12" },
  { src: "/photos/cb-07.webp", caption: "Guitarras y voces", span: "md:col-span-6" },
  { src: "/photos/cb-08.webp", caption: "El pulso del son", span: "md:col-span-6" },
  { src: "/photos/cb-09.webp", caption: "El oficio del bolero", span: "md:col-span-6" },
  { src: "/photos/cb-10.webp", caption: "Casi treinta años de canciones", span: "md:col-span-6" },
  { src: "/photos/cb-11.webp", caption: "Toda la música, en directo", span: "md:col-span-12" },
  { src: "/photos/cb-12.webp", caption: "Percusión y alma", span: "md:col-span-12" },
  { src: "/photos/cb-13.webp", caption: "Las congas del son", span: "md:col-span-6" },
  { src: "/photos/cb-14.webp", caption: "Guitarra y voz, en primer plano", span: "md:col-span-6" },
  { src: "/photos/cb-15.webp", caption: "Gracias por vuestra asistencia", span: "md:col-span-12" },
  { src: "/photos/cb-16.webp", caption: "El saludo final", span: "md:col-span-6" },
  { src: "/photos/cb-17.webp", caption: "Hasta la próxima", span: "md:col-span-6" },
  { src: "/photos/cb-18.webp", caption: "El sentimiento hecho canción", span: "md:col-span-12" },
  { src: "/photos/cb-19.webp", caption: "El sentimiento del bolero", span: "md:col-span-6" },
  { src: "/photos/cb-20.webp", caption: "Maestría con las maracas", span: "md:col-span-6" },
  { src: "/photos/cb-21.webp", caption: "El espectáculo, a escena", span: "md:col-span-12" },
  { src: "/photos/cb-22.webp", caption: "Cada voz, con sentimiento", span: "md:col-span-8 md:row-span-2" },
  { src: "/photos/cb-23.webp", caption: "El requinto, protagonista", span: "md:col-span-4 md:row-span-2" },
  { src: "/photos/cb-24.webp", caption: "Tres voces, un sentimiento", span: "md:col-span-6" },
  { src: "/photos/cb-25.webp", caption: "La guitarra que acompaña", span: "md:col-span-6" },
  { src: "/photos/cb-26.webp", caption: "El bolero, a pleno pulmón", span: "md:col-span-6" },
  { src: "/photos/cb-27.webp", caption: "El ritmo, firme en el bajo", span: "md:col-span-6" },
  { src: "/photos/cb-28.webp", caption: "Mirada de concierto", span: "md:col-span-12" },
];

export const Gallery = () => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (active) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [active]);

  return (
    <section id="galeria" className="relative py-24 sm:py-32 bg-coal/40">
      <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
        <SectionHeader
          eyebrow="Galería"
          title={
            <>
              Instantáneas con <span className="italic text-gold">alma</span>.
            </>
          }
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 auto-rows-[240px] gap-4">
          {PHOTOS.map((p, i) => (
            <Reveal key={p.src} delay={Math.min(i * 0.03, 0.24)} className={p.span}>
              <button
                data-testid={`gallery-item-${i + 1}`}
                onClick={() => setActive(p)}
                className="group relative block h-full w-full overflow-hidden"
              >
                <img src={p.src} alt={p.caption} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <p className="absolute bottom-4 left-4 font-serif italic text-lg text-cream opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  {p.caption}
                </p>
                <span className="absolute top-4 right-4 h-2 w-2 bg-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500 rotate-45" aria-hidden="true" />
                <PhotoMark className="bottom-3 right-3 h-10 w-auto opacity-90" />
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] bg-ink/[0.96] backdrop-blur-sm flex flex-col items-center justify-center p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
          >
            <button
              data-testid="lightbox-close-button"
              className="absolute top-6 right-6 text-cream/70 hover:text-gold transition-colors p-2"
              aria-label="Cerrar"
            >
              <X size={30} strokeWidth={1.5} />
            </button>
            <motion.img
              src={active.src}
              alt={active.caption}
              className="max-h-[78vh] max-w-[92vw] object-contain"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            />
            <p className="mt-5 font-serif italic text-lg text-sand">{active.caption}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
